import React from 'react';
import { Link } from 'react-router-dom';
import './DementiaCare.css';

function DementiaCare() {
  return (
    <div className="dementia-care-page">
      {/* Header/Navigation */}
      <header className="header">
        <nav className="navbar">
          <div className="logo">
            <Link to="/"><h1>Malach <span>Healthcare</span></h1></Link>
          </div>
          <ul className="nav-links">
            <li><Link to="/">Home</Link></li>
            <li><Link to="/#services">Services</Link></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul>
          <Link to="/#contact" className="btn btn-primary">Book Consultation</Link>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="service-hero dementia-hero">
        <div className="service-hero-content">
          <h1>Compassionate Dementia Care That Honors the Whole Person</h1>
          <p>Patience, dignity, and deep respect for each individual's story</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-content">
        <div className="service-intro">
          <p className="intro-highlight">
            When someone you love is living with dementia, everyday life can feel overwhelming. 
            You may be carrying constant worry, exhaustion, guilt, and fear, wondering if you're 
            doing enough, or if you're doing the right thing.
          </p>
          <p>
            At Malach Healthcare, we understand that dementia doesn't just affect one person—it 
            touches the entire family. That's why our dementia care is built around compassion, 
            patience, and deep respect for each individual's story.
          </p>
        </div>

        <div className="family-struggles">
          <h2>We Understand What Families Are Facing</h2>
          <p>
            Caring for a loved one with dementia can be emotionally and physically draining. 
            Families often come to us feeling:
          </p>
          <div className="struggles-grid">
            <div className="struggle-item">
              <span className="struggle-icon">❓</span>
              <p>Unsure how to manage memory loss, confusion, or changes in behavior</p>
            </div>
            <div className="struggle-item">
              <span className="struggle-icon">😔</span>
              <p>Exhausted from round-the-clock caregiving</p>
            </div>
            <div className="struggle-item">
              <span className="struggle-icon">💔</span>
              <p>Afraid of losing connection with the person they love</p>
            </div>
            <div className="struggle-item">
              <span className="struggle-icon">🛡️</span>
              <p>Worried about safety, dignity, and quality of life</p>
            </div>
          </div>
          <p className="struggles-message">
            Malach Healthcare exists to walk beside you during this difficult journey—offering 
            professional support while honoring the love you already give.
          </p>
        </div>

        <div className="care-approach">
          <h2>Care That Sees the Person, Not Just the Condition</h2>
          <p className="approach-intro">
            Dementia may change memory and behavior, but it does not take away a person's worth. 
            Our caregivers are trained to provide care that is:
          </p>
          <div className="approach-grid">
            <div className="approach-item">
              <div className="approach-icon">🤲</div>
              <h3>Patient and Understanding</h3>
              <p>Even during moments of confusion or distress</p>
            </div>
            <div className="approach-item">
              <div className="approach-icon">👤</div>
              <h3>Personalized</h3>
              <p>Respecting routines, preferences, and life history</p>
            </div>
            <div className="approach-item">
              <div className="approach-icon">💜</div>
              <h3>Gentle and Reassuring</h3>
              <p>Promoting calm and emotional safety</p>
            </div>
            <div className="approach-item">
              <div className="approach-icon">✨</div>
              <h3>Focused on Dignity</h3>
              <p>Never treating your loved one as "just a condition"</p>
            </div>
          </div>
          <p className="approach-note">
            We take time to build trust and familiarity, helping your loved one feel safe, 
            valued, and understood.
          </p>
        </div>

        <div className="peace-of-mind">
          <h2>Peace of Mind for Families</h2>
          <p>
            Choosing dementia care is a deeply emotional decision. At Malach Healthcare, 
            families find relief knowing their loved one is:
          </p>
          <ul className="peace-list">
            <li>In the hands of skilled, compassionate professionals</li>
            <li>Supported in a safe, structured, and nurturing environment</li>
            <li>Treated with kindness, respect, and empathy every day</li>
          </ul>
          <p>
            We also support you by communicating openly, listening to your concerns, and 
            working together as a care team.
          </p>
        </div>

        <div className="meaningful-moments">
          <h2>Creating Meaningful Moments</h2>
          <p>
            Our goal is not only to meet medical and daily care needs, but to create moments 
            of connection, comfort, and joy. Even in the challenges of dementia, meaningful 
            life moments are still possible.
          </p>
        </div>

        <div className="beliefs-section">
          <h2>What We Believe</h2>
          <div className="beliefs-grid">
            <div className="belief-item">
              <span className="belief-icon">💝</span>
              <p>Every person deserves compassionate care</p>
            </div>
            <div className="belief-item">
              <span className="belief-icon">🤝</span>
              <p>Families deserve support, not judgment</p>
            </div>
            <div className="belief-item">
              <span className="belief-icon">👨‍👩‍👧‍👦</span>
              <p>No one should face dementia alone</p>
            </div>
          </div>
        </div>

        <div className="closing-message">
          <p>
            If your family is facing the challenges of dementia care, Malach Healthcare is 
            here to help. We invite you to reach out, ask questions, and discover how our 
            compassionate approach can bring comfort to your loved one and peace to your heart.
          </p>
          <p className="tagline">
            Malach Healthcare—compassionate dementia care you can trust.
          </p>
          <Link to="/#contact" className="btn btn-primary btn-large">Book A Free Consultation</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <div className="footer-section">
            <h3>Malach <span>Healthcare</span></h3>
            <p>Empowering golden years with compassionate care.</p>
            <div className="social-links">
              <a href="#facebook">📘</a>
              <a href="#twitter">🐦</a>
              <a href="#instagram">📷</a>
              <a href="#linkedin">💼</a>
            </div>
          </div>
          <div className="footer-section">
            <h4>Quick Links</h4>
            <ul>
              <li><Link to="/">Home</Link></li>
                <li><Link to="/#services">Services</Link></li>
              <li><Link to="/#contact">Contact</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Our Services</h4>
            <ul>
              <li><Link to="/personal-care">Personal Care</Link></li>
              <li><Link to="/companion-care">Companion Care</Link></li>
              <li><Link to="/dementia-care">Dementia Care</Link></li>
              <li><Link to="/specialty-care">Specialty Care</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact Info</h4>
            <p>📍 740 Main St<br />Waltham, MA 02451</p>
            <p>📞 (781) 697-6330</p>
            <p>✉️ info@malachhealthcare.com</p>
          </div>
        </div>
        <div className="footer-bottom">
          <p>Copyright © 2025 | All Rights Reserved | Malach Healthcare</p>
        </div>
      </footer>
    </div>
  );
}

export default DementiaCare;
